import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Datetime from 'react-datetime';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import cn from 'classnames';
import { format } from 'date-fns';
import { get } from 'lodash';
import moment from 'moment';

import LoansAgreementModal from 'components/LoansAgreementModal';
import CancelPaymentModal from 'components/CancelPaymentModal';
import Validator from 'components/Validator/Validator';
import { Button, ButtonLink } from 'components/Button';
import Select from 'components/Form/Select';
import Input from 'components/Form/Input';
import { getCurrentDate } from 'utils/func';
import { formatCurrency } from 'utils/formatCurrency';
import { currencyMask, floatUnmask } from 'utils/masks';
import { makeQuickpayPaymentAction } from 'actions/quickpay';

import styles from './styles.module.scss';
import makePaymentSchema from './schemas/makePaymentSchema';

const SimplePay = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const quickpayData = useSelector((state) => state.quickpay.data);
  const [paymentModal, setPaymentModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const {
    validator: {
      values,
      setValues,
      onChangeHandler,
      validate,
      errors,
    },
  } = props;

  useEffect(() => {
    const primaryPaymentMethod = (quickpayData.paymentAccounts || []).find((acc) => acc.isPrimary);
    setValues({
      paymentAccountId: primaryPaymentMethod?.accountId,
      applyDate: getCurrentDate(),
      amount: quickpayData.totalDue,
      selectedAmountOption: '0',
      rawData: quickpayData,
    });

    if (!primaryPaymentMethod) setIsEdit(true);
  }, []);

  const allPaymentMethods = (quickpayData?.paymentAccounts || []).map((acc) => ({
    value: acc.accountId,
    title: `${(acc.accountType)} ${acc.category === 'bankcard' ? `xxxx-xxxx-xxxx-${acc.cardNumberLast4}` : `xxxx${acc.accountNumberLast4}`}`,
  }));

  allPaymentMethods.push({
    value: 'addPaymentMethod',
    title: '+ Add Payment Method ...',
  });

  const paymentAmounts = [
    {
      title: `Total Due - $${quickpayData.totalDue}`,
      value: '0',
    },
    {
      title: `Past Due (including fees) - $${formatCurrency(quickpayData.amountPastDue + quickpayData.dueFees, 2)}`,
      value: '1',
    },
    {
      title: 'Other Amount',
      value: '2',
    },
  ];

  const toggleModal = (type) => {
    if (type === 'cancel') {
      setCancelModal(!cancelModal);
      setPaymentModal(false);
    } else {
      setPaymentModal(!paymentModal);
      setCancelModal(false);
    }
  };

  const getAmountToPay = () => {
    let nextAmount = 0;
    switch (values.selectedAmountOption) {
      case '0':
        nextAmount = quickpayData.totalDue - quickpayData.dueFees;
        break;
      case '1':
        nextAmount = quickpayData.amountPastDue + quickpayData.dueFees;
        break;
      case '2':
        nextAmount = values.customPaymentAmount;
        break;
      default:
        break;
    }
    return Number(nextAmount);
  };

  const toggleEdit = () => {
    setIsEdit(!isEdit);
  };

  const renderFromAccount = () => {
    const selectedMethod = allPaymentMethods.find((acc) => acc.value === values.paymentAccountId);
    return selectedMethod?.title;
  };

  const handleInputChange = (event) => {
    switch (event.target.name) {
      case 'payFromAccount':
      case 'paymentAccountId':
        if (event.target.value === 'addPaymentMethod') {
          history.push('/quickpay/add-payment-method');
        } else {
          onChangeHandler(event.target.name, event.target.value);
        }
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  };

  const handleBlur = (event) => {
    onChangeHandler(event.target.name, formatCurrency(floatUnmask(event.target.value || '0'), 2));
  };

  const handleDateChange = (date) => {
    if (date && typeof date.toISOString === 'function') {
      onChangeHandler('applyDate', format(new Date(date.toISOString()), 'MM/dd/yyyy'));
    } else {
      onChangeHandler('applyDate', date);
    }
  };

  const validateDate = (current) => {
    if (get(quickpayData, 'daysPastDue', 0) > 45) {
      return current.isAfter(moment().subtract(1, 'day')) && current.isBefore(moment());
    }
    return current.isAfter(moment().subtract(1, 'day')) && current.isBefore(moment().add(45 - get(quickpayData, 'daysPastDue', 0), 'days'));
  };

  const handlePostPayment = () => {
    if (validate().isValid) {
      setIsLoading(true);
      setErrorMsg('');
      const selectedAccount = quickpayData.paymentAccounts.find((acc) => acc.accountId === values.paymentAccountId);
      const paymentMethodCode = selectedAccount.category === 'bankcard' ? 'debit-card' : 'bank-account';
      let amountToPay = getAmountToPay();
      const fees = quickpayData.dueFees;

      if (values.selectedAmountOption === '1') {
        amountToPay = quickpayData.amountPastDue;
      } else if (values.selectedAmountOption === '2') {
        quickpayData.dueFees = 0;
      }

      dispatch(makeQuickpayPaymentAction({
        data: {
          paymentAccountId: values.paymentAccountId,
          paymentMethodCode,
          applyDate: moment(values.applyDate).format('YYYY-MM-DD'),
          amount: amountToPay,
          fees,
          isPayoff: false,
        },
        success: () => {
          history.push('/quickpay/payment-submitted');
          setIsLoading(false);
        },
        fail: (err) => {
          setErrorMsg(get(err, 'data.message') || get(err, 'data.errorMessages.0') || 'An error occured.');
          setIsLoading(false);
        },
      }));
    }
  };

  return (
    <div className={cn(styles.quickpay, styles.simplepay)}>
      <div className={cn(styles.content, 'text-center')}>
        <h2>
          Hey
          {' '}
          {quickpayData.firstName}
          !
        </h2>
        <p>
          <small>
            You have a payment that is due on
            {quickpayData?.nextPaymentDate ? ` ${moment(new Date(quickpayData.nextPaymentDate)).format('MM/DD/YYYY')} ` : ' '}
            in the amount of
          </small>
        </p>
        <span className={styles.paymentAmount}>
          $
          {formatCurrency(quickpayData.totalDue, 2)}
        </span>
        <div className={styles.paymentInfo}>
          <h1>Make a one-time payment</h1>
          {isEdit ? (
            <div>
              <Select
                className="mb-2"
                name="paymentAccountId"
                data={allPaymentMethods}
                value={values.paymentAccountId}
                onChange={handleInputChange}
                label="Pay From Account"
                isRequired
                hasError={!!errors.paymentAccountId}
                errorMessage={errors.paymentAccountId}
              />
              <div className="modal-dialog">
                <label className={cn('has-value', 'hasValue', 'w-100', 'mb-1', errors?.applyDate ? 'required' : '')}>
                  <span>
                    Payment Date
                    <em>Required</em>
                  </span>
                  <Datetime
                    name="applyDate"
                    label="Payment Date"
                    value={values.applyDate}
                    onChange={handleDateChange}
                    closeOnSelect
                    dateFormat="MM/DD/YYYY"
                    timeFormat={false}
                    placeHolder="MM/DD/YYYY"
                    isValidDate={validateDate}
                    renderInput={(propsInput) => (
                      <Input
                        isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                        isRequired
                        hasError={!!errors.applyDate}
                        errorMessage={errors.applyDate}
                        {...propsInput}
                      />
                    )}
                  />
                </label>
              </div>
              <Select
                className="mb-2"
                name="selectedAmountOption"
                label="Payment Amount"
                data={paymentAmounts}
                value={values.selectedAmountOption}
                onChange={handleInputChange}
                isRequired
                hasError={!!errors.selectedAmountOption}
                errorMessage={errors.selectedAmountOption}
              />
              {values.selectedAmountOption === '2' ? (
                <Input
                  label="Custom Payment Amount"
                  name="customPaymentAmount"
                  value={values.customPaymentAmount}
                  onChange={handleInputChange}
                  hasError={!!errors.customPaymentAmount}
                  errorMessage={errors.customPaymentAmount}
                  onBlur={handleBlur}
                  isMasked={currencyMask}
                  isRequired
                />
              ) : null}
            </div>
          ) : (
            <table>
              <tbody>
                <tr>
                  <td>From Account</td>
                  <td className={styles.paymentCell}>{renderFromAccount()}</td>
                </tr>
                <tr>
                  <td>Date</td>
                  <td>{values.applyDate}</td>
                </tr>
                <tr>
                  <td>Amount</td>
                  <td>
                    $
                    {formatCurrency(values.amount, 2)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          {!isEdit && <span className={styles.anchorBtn} onClick={toggleEdit}>EDIT</span>}
        </div>
      </div>
      <div className={styles.stickyFooter}>
        {errorMsg ? (
          <div className={cn(styles.error, 'mb-1')}>
            {errorMsg}
          </div>
        ) : null}
        <small className="text-center d-block mb-1">
          By clicking &#34;Make Payment&#34;, I authorize LendingUSA to make a one-time debit and/or charge from my account as designated above.
        </small>
        <Button
          color="primary"
          className="w-100 mt-1 mb-2"
          onClick={handlePostPayment}
          isLoading={isLoading}
        >
          Make Payment
        </Button>
        <div style={{ textAlign: 'center', marginTop: 5 }}>
          <ButtonLink
            className="d-block text-center btn-link"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleModal('payment')}
          >
            How will my payment be applied?
          </ButtonLink>
        </div>
        <div style={{ textAlign: 'center', marginTop: 5 }}>
          <ButtonLink
            className="d-block text-center btn-link"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleModal('cancel')}
          >
            How can I cancel my payment?
          </ButtonLink>
        </div>
      </div>
      <LoansAgreementModal
        className={styles.modal}
        isOpen={paymentModal}
        toggle={() => toggleModal('payment')}
        paddingTop={1}
        showCloseBtn
        footerLink={(
          <div style={{ textAlign: 'center', marginTop: 5 }}>
            <ButtonLink
              className="d-block text-center btn-link pt-1 pb-1"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleModal('cancel')}
            >
              How can I cancel my payment?
            </ButtonLink>
          </div>
        )}
      />
      <CancelPaymentModal
        className={styles.modal}
        isOpen={cancelModal}
        toggle={() => toggleModal('cancel')}
        paddingTop={1}
        showCloseBtn
        footerLink={(
          <div style={{ textAlign: 'center', marginTop: 5 }}>
            <ButtonLink
              className="d-block text-center btn-link pt-1 pb-1"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleModal('payment')}
            >
              How will my payment be applied?
            </ButtonLink>
          </div>
        )}
      />
    </div>
  );
};

SimplePay.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default compose(Validator(makePaymentSchema))(SimplePay);
